import { Typography } from '@mui/material';
import classNames from 'classnames';
import { isEqual, isRegExp } from 'lodash-es';
import React, { Component, ReactNode } from 'react';

import {
  defaultMatchers,
  IocMatch,
  IocType,
  Match,
} from '../ContextAction/utils';
import css from './IocContent.module.scss';

export interface IocContentClickEvent {
  matches: IocType[];
  content: string;
  nativeEvent: React.MouseEvent;
}

export interface IocContentProps {
  children: ReactNode;
  hoverMatch?: boolean;
  matchers?: IocMatch[];
  value?: string;
  onClick?: (event: IocContentClickEvent) => void;
  onMatchesChange?: (matches: Match[]) => void;
}

interface IocContentState {
  matches: IocType[];
}

export class IocContent extends Component<IocContentProps, IocContentState> {
  static defaultProps: Partial<IocContentProps> = {
    hoverMatch: false,
    onClick: () => undefined,
    onMatchesChange: () => undefined,
  };

  static getDerivedStateFromProps(
    props: IocContentProps,
    state: IocContentState
  ) {
    const { children, matchers, onMatchesChange, value } = props;
    let combinedMatchers = [...defaultMatchers];

    if (matchers) {
      combinedMatchers = [...combinedMatchers, ...matchers];
    }

    // If there is no test function, assume the match type always passes.
    const rawValue = value ?? children;
    const validType = ['string', 'number'].includes(typeof rawValue);
    const matches =
      (validType &&
        combinedMatchers
          ?.filter(({ test: matchTest }) =>
            matchTest
              ? isRegExp(matchTest)
                ? matchTest.test(`${rawValue}`)
                : matchTest(`${rawValue}`)
              : true
          )
          .map(({ type }) => type)) ||
      [];

    if (!isEqual(matches, state.matches) && onMatchesChange) {
      onMatchesChange(
        matches.map((type) => ({
          type,
          content: rawValue as string,
        }))
      );
    }

    return { matches };
  }

  state: IocContentState = { matches: [] };

  onClick(matches: IocType[], nativeEvent: React.MouseEvent<HTMLSpanElement>) {
    const { onClick, children, value } = this.props;

    if (onClick && matches.length) {
      onClick({
        matches,
        nativeEvent,
        content: (value ?? children) as string,
      });
    }
  }

  render() {
    const { children } = this.props;
    const { matches } = this.state;
    const hasMatches = !!matches.length;

    return (
      <div className={css.iocContent}>
        <Typography
          className={classNames({
            [css.match]: hasMatches,
          })}
          component='span'
          fontSize='inherit'
          onClick={(e) => this.onClick(matches, e)}
        >
          {children}
        </Typography>
      </div>
    );
  }
}
